import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enquiryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/Toast';
import { LABELS } from '../../constants/enquiryFormOptions';
import {
  fieldStyle,
  StatusBadge,
  thStyle,
  tdStyle,
} from '../../utils/enquiryAdminUi';

export default function EnquiryListPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    search: '',
    status: '',
    course: '',
    interestLevel: '',
  });

  const load = () => {
    setLoading(true);
    enquiryAPI
      .list(filters)
      .then((res) => setData(res.data))
      .catch(() => showToast('Failed to load enquiries', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.status, filters.course, filters.interestLevel]);

  useEffect(() => {
    const itemIds = new Set(data.items.map((item) => item._id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => itemIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [data.items]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((f) => ({ ...f, page: 1 }));
    load();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPageSelected =
    data.items.length > 0 && data.items.every((item) => selectedIds.has(item._id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(data.items.map((item) => item._id)));
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0 || !bulkAction) return;
    if (bulkAction !== 'delete' && !bulkValue) return;

    if (bulkAction === 'delete') {
      const confirmed = window.confirm(
        `Delete ${selectedIds.size} enquiry(s)? This action cannot be undone.`
      );
      if (!confirmed) return;
    }

    setBulkApplying(true);
    try {
      const res = await enquiryAPI.bulk({
        ids: [...selectedIds],
        action: bulkAction,
        value: bulkAction === 'delete' ? undefined : bulkValue,
      });
      setSelectedIds(new Set());
      setBulkAction('');
      setBulkValue('');
      showToast(`Updated ${res.data.modified} enquiry(s)`, 'success');
      load();
    } catch {
      showToast('Bulk action failed', 'error');
    } finally {
      setBulkApplying(false);
    }
  };

  const bulkNeedsValue = bulkAction === 'status' || bulkAction === 'interestLevel';
  const canApplyBulk =
    selectedIds.size > 0 && bulkAction && (!bulkNeedsValue || bulkValue) && !bulkApplying;

  const { pagination } = data;

  if (loading) {
    return <div className="loading">Loading enquiries...</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div
          style={{
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
              Student Enquiries
            </h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              {pagination?.total != null
                ? `${pagination.total} total submission${pagination.total !== 1 ? 's' : ''}`
                : 'Manage course enquiry leads'}
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSearch}
          style={{
            marginBottom: '20px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'flex-end',
          }}
        >
          <input
            type="text"
            placeholder="Search name, email, mobile, enquiry no."
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            style={{ ...fieldStyle, flex: '1 1 220px', minWidth: '200px' }}
            onFocus={(e) => {
              e.target.style.borderColor = '#667eea';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
            }}
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
            style={{ ...fieldStyle, flex: '0 1 160px' }}
          >
            <option value="">All statuses</option>
            {Object.entries(LABELS.status).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={filters.course}
            onChange={(e) => setFilters((f) => ({ ...f, course: e.target.value, page: 1 }))}
            style={{ ...fieldStyle, flex: '0 1 180px' }}
          >
            <option value="">All courses</option>
            {Object.entries(LABELS.course).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={filters.interestLevel}
            onChange={(e) =>
              setFilters((f) => ({ ...f, interestLevel: e.target.value, page: 1 }))
            }
            style={{ ...fieldStyle, flex: '0 1 160px' }}
          >
            <option value="">All interest</option>
            {Object.entries(LABELS.interestLevel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-primary" style={{ textTransform: 'none' }}>
            Search
          </button>
        </form>

        {selectedIds.size > 0 && (
          <div
            style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f7fafc',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
              {selectedIds.size} selected
            </span>
            <select
              value={bulkAction}
              onChange={(e) => {
                setBulkAction(e.target.value);
                setBulkValue('');
              }}
              style={{ ...fieldStyle, flex: '0 1 180px' }}
            >
              <option value="">Choose action…</option>
              {user?.role === 'admin' && <option value="delete">Delete</option>}
              <option value="status">Set status</option>
              <option value="interestLevel">Set interest level</option>
            </select>
            {bulkAction === 'status' && (
              <select
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                style={{ ...fieldStyle, flex: '0 1 160px' }}
              >
                <option value="">Select status…</option>
                {Object.entries(LABELS.status).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            )}
            {bulkAction === 'interestLevel' && (
              <select
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                style={{ ...fieldStyle, flex: '0 1 160px' }}
              >
                <option value="">Select interest…</option>
                {Object.entries(LABELS.interestLevel).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className={bulkAction === 'delete' ? 'btn btn-danger' : 'btn btn-primary'}
              disabled={!canApplyBulk}
              onClick={applyBulk}
              style={{ textTransform: 'none' }}
            >
              {bulkApplying ? 'Applying…' : 'Apply'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setSelectedIds(new Set())}
              style={{ textTransform: 'none' }}
            >
              Clear
            </button>
          </div>
        )}

        {data.items.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 24px',
              color: '#718096',
              fontSize: '14px',
            }}
          >
            <p style={{ marginBottom: '8px', fontSize: '15px' }}>No enquiries found</p>
            <p style={{ fontSize: '13px', color: '#a0aec0' }}>
              Try adjusting filters or wait for new submissions at /enquiry
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ ...thStyle, width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all on this page"
                    />
                  </th>
                  <th style={thStyle}>Enquiry No.</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Mobile</th>
                  <th style={thStyle}>Course</th>
                  <th style={thStyle}>Interest</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Submitted</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr
                    key={item._id}
                    style={{
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor: selectedIds.has(item._id) ? '#eef2ff' : 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedIds.has(item._id)) {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selectedIds.has(item._id)
                        ? '#eef2ff'
                        : 'transparent';
                    }}
                  >
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item._id)}
                        onChange={() => toggleSelect(item._id)}
                        aria-label={`Select ${item.enquiryNumber}`}
                      />
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600', color: '#667eea' }}>
                      {item.enquiryNumber}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600', color: '#2d3748' }}>
                      {item.personal?.fullName}
                    </td>
                    <td style={tdStyle}>{item.personal?.mobile}</td>
                    <td style={tdStyle}>
                      {LABELS.course[item.coursePreference?.courseInterested] || '—'}
                    </td>
                    <td style={tdStyle}>
                      {item.counseling?.interestLevel ? (
                        <StatusBadge
                          variant="interest"
                          value={item.counseling.interestLevel}
                          label={LABELS.interestLevel[item.counseling.interestLevel]}
                        />
                      ) : (
                        <span style={{ color: '#a0aec0' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <StatusBadge
                        variant="status"
                        value={item.status}
                        label={LABELS.status[item.status]}
                      />
                    </td>
                    <td style={{ ...tdStyle, color: '#718096' }}>
                      {new Date(item.submittedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/enquiries/${item._id}`)}
                        style={{
                          padding: '6px 14px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination?.pages > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#f7fafc',
              borderRadius: '8px',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <span style={{ fontSize: '13px', color: '#718096' }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                disabled={filters.page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                style={{
                  padding: '8px 12px',
                  backgroundColor: filters.page <= 1 ? '#e2e8f0' : 'white',
                  color: filters.page <= 1 ? '#a0aec0' : '#667eea',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: filters.page <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ← Previous
              </button>
              <button
                type="button"
                disabled={filters.page >= pagination.pages}
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                style={{
                  padding: '8px 12px',
                  backgroundColor: filters.page >= pagination.pages ? '#e2e8f0' : 'white',
                  color: filters.page >= pagination.pages ? '#a0aec0' : '#667eea',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: filters.page >= pagination.pages ? 'not-allowed' : 'pointer',
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
